/**
 * MIND Chat Widget for Vibe Designer
 *
 * Renders a floating action button (🧠) that opens a sliding panel with
 * MIND Chat embedded via iframe. Communicates context to MIND Chat via
 * postMessage.
 *
 * THE MIND is the platform's primary knowledge system (one of six:
 * BRAIN, MIND, CORTEX, SIGHT, COGNITION, WIKI). Phase E (2026-05-06)
 * shipped the backend AI Chat path's swap from `_query_brain` to
 * `_query_mind` in services/llm_agent.py — this widget was already
 * the user-facing surface for MIND chat (unchanged in Phase E).
 *
 * MIND Chat service:
 *   - Admin (gamemagick-ai): http://192.168.1.29:9377/chat (via tunnel: localhost:9377)
 *   - Tenant (BB): http://192.168.1.29:9378/chat (via tunnel: localhost:9378)
 *
 * Environment:
 *   - VITE_MIND_CHAT_URL: MIND Chat URL (default: http://localhost:9377/chat)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { useMindChatUiStore } from '../../stores/mindChatUiStore';

// Phase E.2 (2026-05-06): same-origin proxy. The widget hits a relative
// URL `/mind-chat/chat`; vibe-frontend nginx proxies to per-tenant
// t440-mind-chat-{domain}:8000 container. Browser sees same-origin load,
// bypassing cross-subdomain iframe blocking by modern browsers (Firefox
// HTTPS-First, Safari Lockdown, Chrome 3PCD partitioning). The previous
// E.21.8 hostname-regex behavior produced cross-origin URLs that triggered
// the M5b acceptance-gate failure documented in the v1.0.0 → v1.1.0
// reviewer trail.
// Co-exported with the MindChatWidget component for unit testability — Fast
// Refresh disable is intentional, not a code smell.
// eslint-disable-next-line react-refresh/only-export-components
export function resolveMindChatUrl(_hostname?: string): string {
  // VITE_MIND_CHAT_URL is preserved as a local-dev escape hatch.
  // Production paths (LAN + VPS) all return the relative same-origin URL.
  const envUrl = import.meta.env.VITE_MIND_CHAT_URL;
  if (envUrl) return envUrl;
  return '/mind-chat/chat';
}

const MIND_CHAT_URL = resolveMindChatUrl();

// Phase E.2 CRITICAL-3 (Reviewer 1 v2): post-proxy iframe is same-origin
// with the parent. postMessage targetOrigin must reflect that. The
// previous `new URL(MIND_CHAT_URL).origin` would throw on a relative URL
// AND was the wrong cross-origin form even when URL was absolute.
// `window.location.origin` is what the browser will report as the iframe's
// origin under the same-origin proxy.
const MIND_CHAT_ORIGIN =
  typeof window !== 'undefined' ? window.location.origin : '';

interface MindChatContext {
  type: 'vibe-designer-context';
  timestamp: string;
  source: string;
  project?: {
    id: string;
    name: string;
    framework: string;
  };
  activeFile?: {
    // E.18 (2026-04-29): NH.E18.G0 default #2 — drop `content: string`. Sending
    // full file content over postMessage on every iframe-ready event is
    // excessive token-load on the LLM system prompt. Path + language are the
    // contract; consumers can ask for content via a separate explicit channel.
    path: string;
    language: string;
  };
}

export function MindChatWidget() {
  // 1.555.0: open-state lifted to mindChatUiStore so the TopBar 🧠 button drives
  // it (the FAB used to overlap the Pi composer). This component now renders
  // ONLY the panel; the trigger lives in TopBar.
  const isOpen = useMindChatUiStore((s) => s.isOpen);
  const close = useMindChatUiStore((s) => s.close);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [panelWidth, setPanelWidth] = useState(480);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);

  // Listen for MIND Chat ready signal. E.12 (2026-04-29): handler only flips
  // the ready flag; the postMessage side effect is in a separate useEffect
  // below that watches isReady (closes the pre-existing stale-closure bug
  // where calling sendContext here used a closure with isReady=false).
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (!event.data || typeof event.data !== 'object') return;

      if (event.data.type === 'mind-chat-ready') {
        setIsReady(true);
      }

      if (event.data.type === 'mind-chat-dim-preference') {
        // Could apply dim to overlay
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Collect context from Vibe Designer state. E.18 (2026-04-29): reads real
  // project + active file from useProjectStore (replaces E.12-era stub).
  const collectContext = useCallback((): MindChatContext => {
    const { currentProject, getActiveFile } = useProjectStore.getState();
    const activeFile = getActiveFile();
    return {
      type: 'vibe-designer-context',
      timestamp: new Date().toISOString(),
      source: 'vibe-designer',
      project: currentProject
        ? {
            id: currentProject.id,
            name: currentProject.name,
            framework: currentProject.framework,
          }
        : undefined,
      activeFile: activeFile
        ? {
            path: activeFile.path,
            language: activeFile.language,
          }
        : undefined,
    };
  }, []);

  // Send context to MIND Chat iframe.
  const sendContext = useCallback(() => {
    if (iframeRef.current?.contentWindow && isReady) {
      const context = collectContext();
      iframeRef.current.contentWindow.postMessage(context, MIND_CHAT_ORIGIN);
    }
  }, [isReady, collectContext]);

  // E.12 (2026-04-29): post context once iframe signals ready. Watching
  // isReady avoids the stale-closure trap that handleMessage hit pre-fix —
  // when isReady flips false→true, the sendContext closure recomputes (its
  // isReady dep changed) and this effect re-runs, firing postMessage with
  // the correct guard.
  useEffect(() => {
    if (isReady) {
      sendContext();
    }
  }, [isReady, sendContext]);

  // Open side-effects, fired on the closed→open EDGE (the store now owns
  // isOpen; the TopBar 🧠 button flips it). Mirrors the old `open()` callback:
  // lazy-load the iframe on first open, else push fresh context if the iframe
  // is already ready. (sendContext-on-first-ready is handled by its own effect
  // below, so this only covers re-opening an already-loaded panel.)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = isOpen;
    if (isOpen && !wasOpen) {
      if (!isLoaded) {
        setIsLoaded(true);
      } else if (isReady) {
        sendContext();
      }
    }
  }, [isOpen, isLoaded, isReady, sendContext]);

  // Resize handle
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizeRef.current = { startX: e.clientX, startWidth: panelWidth };

    const onMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;
      const delta = resizeRef.current.startX - e.clientX;
      const newWidth = Math.min(Math.max(resizeRef.current.startWidth + delta, 320), 900);
      setPanelWidth(newWidth);
    };

    const onUp = () => {
      resizeRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (iframeRef.current) iframeRef.current.style.pointerEvents = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    if (iframeRef.current) iframeRef.current.style.pointerEvents = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [panelWidth]);

  // Escape key closes
  useEffect(() => {
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeydown);
    return () => document.removeEventListener('keydown', onKeydown);
  }, [close]);

  return (
    <>
      {/* 1.555.0: the 🧠 trigger moved to the TopBar (TopBar.tsx, next to Help/
          Settings) via mindChatUiStore — the old fixed bottom-right FAB
          overlapped the Pi chat composer. This component renders ONLY the
          sliding panel now. */}
      {/* Sliding Panel */}
      {isOpen && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
          className="fixed inset-0 z-[9999] bg-transparent transition-[background] duration-300"
        >
          {/* Resize handle — `right` is dynamic (panelWidth state) */}
          <div
            onMouseDown={handleResizeStart}
            className="fixed top-0 w-2 h-full cursor-col-resize z-[10001]"
            style={{ right: panelWidth - 4 }}
          />

          {/* Chat panel — R2 Concern 3: width + #1a1a2e background stay
              INLINE. width is dynamic; #1a1a2e is design-specific and
              does NOT match bg-vibe-bg-secondary (#111113) — would be
              silent visual regression if swapped. */}
          <div
            className={
              'fixed top-0 right-0 h-full ' +
              'shadow-[-4px_0_20px_rgba(0,0,0,0.3)] ' +
              'flex flex-col z-[10000] animate-slide-in-right'
            }
            style={{ width: `${panelWidth}px`, background: '#1a1a2e' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/20">
              <div className="flex items-center gap-2 text-white">
                <span className="text-lg">🧠</span>
                <span className="font-semibold text-sm">MIND Chat</span>
              </div>
              <button
                onClick={close}
                className="bg-transparent border-0 text-vibe-text-secondary cursor-pointer text-xl px-2 py-1"
              >
                &times;
              </button>
            </div>

            {/* Iframe — Phase E.2 invariants: inline width/height/border/
                background MUST stay inline (jsdom test assertions at
                MindChatWidget.test.tsx:308-319 read style.* directly).
                allow="microphone" + src + ref also untouched. */}
            {isLoaded && (
              <iframe
                ref={iframeRef}
                src={MIND_CHAT_URL}
                allow="microphone"
                style={{
                  width: '100%',
                  height: 'calc(100% - 48px)',
                  border: 'none',
                  background: '#0f0f23',
                }}
                title="MIND Chat"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}
